import { Component, inject } from '@angular/core';
import { NgFor } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    FormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { map, startWith } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { StateResponse } from '../../interfaces/StateResponse';
import { State } from '../../interfaces/State';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import * as CVSS from '../../../assets/scripts/cvsscalc31.js';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { Asset } from '../../interfaces/Asset';
import { Observable } from 'rxjs';
import { AssetResponse } from '../../interfaces/AssetResponse';
import { VulnerabilityAssessmentResult } from '../../interfaces/VulnerabilityAssessmentResult';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { VulnerabilityAssessmentResultResponse } from '../../interfaces/VulnerabilityAssessmentResultResponse';
import { HelpRequest } from '../../interfaces/HelpRequest';
import { GlobalConstants } from '../../common/global-constants';

@Component({
    selector: 'app-v-edit-vulnerability',
    standalone: true,
    imports: [
        MatTableModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatIconModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        AsyncPipe,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
        CommonModule,
    ],
    templateUrl: './v-edit-vulnerability.component.html',
    styleUrl: './v-edit-vulnerability.component.scss',
})
export class VEditVulnerabilityComponent {
    specialist: string = GlobalConstants.specialist;
    // vulnerabilityAssessmentId
    vulnerabilityAssessmentId: string = '';

    //vulnerabilityAssessmentResultId
    vulnerabilityAssessmentResultId: string = '';

    // vulnerabilityAssessment
    vulnerabilityAssessment: VulnerabilityAssessment;

    // editVulnerability
    editVulnerability: VulnerabilityAssessmentResult;

    // isToggled
    isToggled = false;

    // Forms
    editVulnerabilityForm: FormGroup;

    // Array of states
    states: State[] = [];

    selectedState: State | undefined;

    // Array of assets
    assets: Asset[] = [];
    assetControl = new FormControl<string | Asset>(
        {
            value: '',
            disabled: !this.isInternal,
        },
        [Validators.required]
    );
    filteredAssets: Observable<Asset[]>;

    // Score
    score: number;
    cvssClassification: string;

    // Help
    help: string = '';

    // Common Vulnerability Scoring System Version 3.1 Calculator
    attackVector: string;
    attackComplexity: string;
    privilegesRequired: string;
    userInteraction: string;
    scope: string;
    confidentiality: string;
    integrity: string;
    availability: string;

    // Show alert
    showAlert: boolean = false;

    // Error message
    errorMsg: string;

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.editVulnerabilityForm = this.fb.group({
            name: [
                { value: '', disabled: !this.isInternal },
                [Validators.required, Validators.maxLength(200)],
            ],
            referenceCode: [
                { value: '', disabled: !this.isInternal },
                [Validators.maxLength(6)],
            ],
            expectedResolutionDate: [{ value: '', disabled: true }, []],
            description: [{ value: '', disabled: !this.isInternal }, []],
            port: [
                { value: '', disabled: !this.isInternal },
                [Validators.min(1), Validators.max(65535)],
            ],
            resolutionDate: [{ value: '', disabled: true }, []],
            impact: [{ value: '', disabled: !this.isInternal }, []],
            protocol: [
                { value: '', disabled: !this.isInternal },
                [Validators.maxLength(30)],
            ],
            state: [
                {
                    value: this.selectedState,
                    disabled:
                        this.roleName == GlobalConstants.supervisor ||
                        this.roleName == GlobalConstants.manager,
                },
                [Validators.required],
            ],
            recommendation: [{ value: '', disabled: !this.isInternal }, []],
            vulnerabilityDate: [
                { value: '', disabled: true },
                [Validators.required],
            ],
            asset: this.assetControl,
            score: [
                { value: this.score, disabled: true },
                [Validators.required],
            ],
            help: [{ value: this.help, disabled: true }, []],
            cvssClassification: [
                this.cvssClassification,
                [Validators.maxLength(80)],
            ],
            attackVector: [
                { value: this.attackVector, disabled: !this.isInternal },
                [],
            ],
            attackComplexity: [
                { value: this.attackComplexity, disabled: !this.isInternal },
                [],
            ],
            privilegesRequired: [
                { value: this.privilegesRequired, disabled: !this.isInternal },
                [],
            ],
            userInteraction: [
                { value: this.userInteraction, disabled: !this.isInternal },
                [],
            ],
            scope: [{ value: this.scope, disabled: !this.isInternal }, []],
            confidentiality: [
                { value: this.confidentiality, disabled: !this.isInternal },
                [],
            ],
            integrity: [
                { value: this.integrity, disabled: !this.isInternal },
                [],
            ],
            availability: [
                { value: this.availability, disabled: !this.isInternal },
                [],
            ],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.vulnerabilityAssessmentId =
                params['vulnerabilityAssessmentId'];
            this.vulnerabilityAssessmentResultId =
                params['vulnerabilityAssessmentResultId'];

            this.apiService
                .getVulnerabilityAssessmentById(
                    this.vulnerabilityAssessmentId,
                    this.roleName == GlobalConstants.internal
                )
                .subscribe((data: VulnerabilityAssessmentResponse) => {
                    this.vulnerabilityAssessment = data.vulnerabilityAssessment;

                    this.apiService
                        .getVulnerabilityAssessmentResultById(
                            this.vulnerabilityAssessmentResultId,
                            this.roleName == GlobalConstants.internal
                        )
                        .subscribe(
                            (data: VulnerabilityAssessmentResultResponse) => {
                                this.editVulnerability =
                                    data.vulnerabilityAssessmentResult;
                                if (this.editVulnerability.asset) {
                                    this.assetControl.setValue(
                                        this.editVulnerability.asset
                                    );
                                    this.assets.push(
                                        this.editVulnerability.asset
                                    );
                                }

                                if (this.editVulnerability.state == undefined) {
                                    this.apiService
                                        .getState()
                                        .subscribe(
                                            (response: StateResponse) => {
                                                this.states.push(
                                                    ...response.states
                                                );
                                                if (
                                                    this.editVulnerability.state
                                                ) {
                                                    this.selectedState =
                                                        this.states.find(
                                                            ({ id }) =>
                                                                id ==
                                                                this
                                                                    .editVulnerability
                                                                    .state?.id
                                                        );
                                                }
                                            }
                                        );
                                } else {
                                    this.apiService
                                        .getStateDestination(
                                            this.editVulnerability.state.id
                                        )
                                        .subscribe(
                                            (response: StateResponse) => {
                                                this.states.push(
                                                    ...response.states
                                                );
                                                this.selectedState =
                                                    this.states.find(
                                                        ({ id }) =>
                                                            id ===
                                                            this
                                                                .editVulnerability
                                                                .state?.id
                                                    );
                                            }
                                        );
                                }

                                if (this.editVulnerability.cvssClassification) {
                                    const cvss =
                                        this.editVulnerability
                                            .cvssClassification.length == 44
                                            ? this.editVulnerability.cvssClassification.substring(
                                                  9,
                                                  44
                                              )
                                            : this.editVulnerability
                                                  .cvssClassification;
                                    this.attackVector = cvss.substring(3, 4);
                                    this.attackComplexity = cvss.substring(
                                        8,
                                        9
                                    );
                                    this.privilegesRequired = cvss.substring(
                                        13,
                                        14
                                    );
                                    this.userInteraction = cvss.substring(
                                        18,
                                        19
                                    );
                                    this.scope = cvss.substring(22, 23);
                                    this.confidentiality = cvss.substring(
                                        26,
                                        27
                                    );
                                    this.integrity = cvss.substring(30, 31);
                                    this.availability = cvss.substring(34, 35);
                                    this.cvssClassification = cvss;
                                }
                            }
                        );
                });

            this.filteredAssets = this.assetControl.valueChanges.pipe(
                startWith(''),
                map((value) => {
                    const name =
                        typeof value === 'string' ? value : value?.name;
                    return name
                        ? this._filter(name as string)
                        : this.assets.slice();
                })
            );
        });
    }

    // ngAfterViewInit
    ngAfterViewInit() {
        console.log(this.editVulnerability);
        console.log(this.editVulnerabilityForm.value);
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    /**
     * isInternal
     */
    get isInternal(): boolean {
        return (localStorage.getItem('roleName') ?? '') !=
            GlobalConstants.internal
            ? false
            : true;
    }

    /**
     * _filter
     */
    private _filter(value: string): Asset[] {
        const filterValue = value.toLowerCase();
        if (filterValue.length < 3) {
            this.assets = [];
            return this.assets;
        } else {
            if (this.isInternal) {
                this.apiService
                    .getAssetsByBranchOficceId(
                        this.vulnerabilityAssessment.branchOffice.id,
                        filterValue
                    )
                    .subscribe((response: AssetResponse) => {
                        if (response && response.assets) {
                            this.assets = [];
                            this.assets.push(...response.assets);
                        }
                    });
            } else {
                this.apiService
                    .getAssetsByPattern(filterValue)
                    .subscribe((response: AssetResponse) => {
                        if (response && response.assets) {
                            this.assets = [];
                            this.assets.push(...response.assets);
                        }
                    });
            }
            return this.assets.filter((asset) =>
                asset.name.toLowerCase().includes(filterValue)
            );
        }
    }

    // searchHelp
    searchHelp() {
        this.help = 'Loading AI..';
        if (this.editVulnerability.id) {
            const iaHelp: HelpRequest = {
                vulnerabilityResultId: this.editVulnerability.id,
            };
            this.apiService
                .postVulnerabilityAssessmentResultHelp(iaHelp)
                .subscribe((help: VulnerabilityAssessmentResultResponse) => {
                    this.help = help.aiResponse.responseMessage;
                });
        }
    }

    // displayFn
    displayFn(asset: Asset): string {
        return asset && asset.name ? asset.name : '';
    }

    // onValChange
    onValChange(name: string, value: string) {
        switch (name) {
            case 'attackVector':
                this.attackVector = value;
                break;
            case 'attackComplexity':
                this.attackComplexity = value;
                break;
            case 'privilegesRequired':
                this.privilegesRequired = value;
                break;
            case 'userInteraction':
                this.userInteraction = value;
                break;
            case 'scope':
                this.scope = value;
                break;
            case 'confidentiality':
                this.confidentiality = value;
                break;
            case 'integrity':
                this.integrity = value;
                break;
            case 'availability':
                this.availability = value;
                break;
        }
        var output = CVSS.CVSS31.calculateCVSSFromMetrics(
            this.attackVector,
            this.attackComplexity,
            this.privilegesRequired,
            this.userInteraction,
            this.scope,
            this.confidentiality,
            this.integrity,
            this.availability
        );

        var result;
        if (output.success === true) {
            if (this.editVulnerability) {
                this.editVulnerability.score = output.baseMetricScore
                    ? parseFloat(output.baseMetricScore)
                    : 0.0;
                this.editVulnerability.cvssClassification = output.vectorString;
            }

            result =
                'Base score is ' +
                output.baseMetricScore +
                '. ' +
                'Base severity is ' +
                output.baseSeverity +
                '. ' +
                'Temporal score is ' +
                output.temporalMetricScore +
                '. ' +
                'Temporal severity is ' +
                output.temporalSeverity +
                '. ' +
                'Environmental score is ' +
                output.environmentalMetricScore +
                '. ' +
                'Environmental severity is ' +
                output.environmentalSeverity +
                '. ' +
                'Vector string is ' +
                output.vectorString +
                '. ';
        } else {
            result =
                "An error occurred. The error type is '" +
                output.errorType +
                "' and the metrics with errors are " +
                output.errorMetrics +
                '.';
            console.error(result);
        }
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.editVulnerabilityForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        // Hide the alert
        this.showAlert = false;

        if (this.isInternal) {
            // Disable the form
            this.editVulnerabilityForm.disable();

            this.editVulnerability.state = this.selectedState;

            if (this.editVulnerability.vulnerabilityDate) {
                const vulnerabilityDate = new Date(
                    this.editVulnerability.vulnerabilityDate
                );
                this.editVulnerability.vulnerabilityDate = vulnerabilityDate
                    .getTime()
                    .toString();
            }

            if (this.editVulnerability.expectedResolutionDate) {
                const expectedResolutionDate = new Date(
                    this.editVulnerability.expectedResolutionDate
                );
                this.editVulnerability.expectedResolutionDate =
                    expectedResolutionDate.getTime().toString();
            }

            if (this.editVulnerability.resolutionDate) {
                const resolutionDate = new Date(
                    this.editVulnerability.resolutionDate
                );
                this.editVulnerability.resolutionDate = resolutionDate
                    .getTime()
                    .toString();
            }

            console.log(this.editVulnerability);

            this.apiService
                .patchVulnerabilityAssessmentResult(
                    this.vulnerabilityAssessmentResultId,
                    this.editVulnerability
                )
                .subscribe({
                    next: (value) => {
                        // Re-enable the form
                        this.editVulnerabilityForm.enable();

                        // Show the alert
                        this.showAlert = true;
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );

                        // Re-enable the form
                        this.editVulnerabilityForm.enable();

                        // Show the alert
                        this.showAlert = true;
                    },
                    complete: () => {
                        this.router.navigateByUrl(
                            `/vulnerability/view-vulnerabilities/${this.vulnerabilityAssessmentId}`
                        );
                    },
                });
        } else if (this.selectedState) {
            const updateState = JSON.stringify({
                id: this.vulnerabilityAssessmentResultId,
                stateId: this.selectedState.id,
            });

            this.apiService
                .putVulnerabilityAssessmentResultState(updateState)
                .subscribe({
                    next: (value) => {
                        // Show the alert
                        this.showAlert = true;
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );

                        // Show the alert
                        this.showAlert = true;
                    },
                    complete: () => {
                        this.router.navigateByUrl(
                            `/vulnerability/view-vulnerabilities/${this.vulnerabilityAssessmentId}`
                        );
                    },
                });
        }
    }

    changeStateAndManageVulnerability() {
        if (this.roleName == GlobalConstants.specialist && this.selectedState) {
            const updateState = JSON.stringify({
                id: this.vulnerabilityAssessmentResultId,
                stateId: this.selectedState.id,
            });

            this.apiService
                .putVulnerabilityAssessmentResultState(updateState)
                .subscribe({
                    next: (value) => {
                        // Show the alert
                        this.showAlert = true;
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );
                        // Show the alert
                        this.showAlert = true;
                    },
                    complete: () => {
                        this.router.navigateByUrl(
                            `/vulnerability/manage-vulnerability/${this.vulnerabilityAssessmentId}/${this.vulnerabilityAssessmentResultId}`
                        );
                    },
                });
        }
    }
}
